import { ClipboardList, Copy, LayoutTemplate, Sparkles } from "lucide-react";
import { useState, type FC, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { firmKnowledgePath } from "@/features/firmKnowledge/routes";

import { ASSISTANT_NAME } from "../model";
import type { NewRequestAction } from "../types";
import { NewRequestDialog } from "./NewRequestDialog";

type Props = {
  /** The menu's trigger, rendered as is (a Button). */
  trigger: ReactNode;
  onNewRequest: (action: NewRequestAction) => void;
  align?: "start" | "end";
};

/**
 * New Request, three ways: a blank intake, one from a template (the firm's or
 * a CaseBlink form), or a brief for Casey to draft. Templates themselves are
 * managed in Firm Knowledge.
 */
export const NewRequestMenu: FC<Props> = ({
  trigger,
  onNewRequest,
  align = "end"
}) => {
  const navigate = useNavigate();
  const [templatesOpen, setTemplatesOpen] = useState(false);
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent align={align} className="w-60">
          <DropdownMenuItem onSelect={() => onNewRequest({ kind: "blank" })}>
            <ClipboardList /> Blank Intake
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setTemplatesOpen(true)}>
            <Copy /> From a Template…
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onNewRequest({ kind: "ai" })}>
            <Sparkles /> Ask {ASSISTANT_NAME} to Draft…
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-fg-3"
            onSelect={() =>
              navigate(firmKnowledgePath({ tab: "intake_templates" }))
            }
          >
            <LayoutTemplate /> Manage Templates in Firm Knowledge
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <NewRequestDialog
        open={templatesOpen}
        onOpenChange={setTemplatesOpen}
        onCreate={onNewRequest}
      />
    </>
  );
};
